import { SectionTitle, Sub, P, FlowSteps } from "./GuideHelpers";
import { glossaryData, pageRelationshipData } from "./constants";

export const SectionWorkflowsGlossary = () => (
  <>
    <SectionTitle id="workflows">Strategic Workflows</SectionTitle>
    <P>
      Signal's pages aren't isolated — they form interconnected workflows. Here are the most common
      strategic patterns:
    </P>

    <Sub>1. New Product Onboarding</Sub>
    <FlowSteps
      items={[
        { actor: "user", label: "Add Hero ASIN in Products Hub" },
        { actor: "signal", label: "Keyword bank auto-populates from SQP report and Signal data lake" },
        { actor: "user", label: "Run Content Scorecard to audit listing quality" },
        { actor: "user", label: "Generate first Landscape to benchmark vs. competitors" },
        { actor: "user", label: "Review SEO Insights (generated after landscape)" },
        { actor: "user", label: "Curate Master List → keywords auto-added to Rank Tracker" },
      ]}
    />

    <Sub>2. Content Optimization Cycle</Sub>
    <FlowSteps
      items={[
        { actor: "signal", label: "SEO Insights identifies a missing high-value keyword" },
        { actor: "user", label: "Create A/B Test to validate the content change" },
        { actor: "user", label: "Monitor rank movement in Rank Tracker" },
        { actor: "signal", label: "Upload results → Signal updates Brand Signals" },
        { actor: "user", label: "Run new Landscape to confirm competitive improvement" },
      ]}
    />

    <Sub>3. Competitive Monitoring</Sub>
    <FlowSteps
      items={[
        { actor: "signal", label: "Dashboard shows rank decline for key keywords" },
        { actor: "user", label: "Open Rank Tracker to identify which products are affected" },
        { actor: "user", label: "Run a Landscape to see if competitors gained ground" },
        { actor: "user", label: "Review SEO Insights for defensive keyword opportunities" },
        { actor: "user", label: "Update Search Terms and listing content accordingly" },
      ]}
    />

    <Sub>4. Backend Search Term Refresh</Sub>
    <FlowSteps
      items={[
        { actor: "user", label: "Ensure at least one landscape exists for the product" },
        { actor: "signal", label: "Generate new Search Term set (auto-filters and deduplicates)" },
        { actor: "signal", label: "Check for prohibited keywords" },
        { actor: "user", label: "Export and paste into Amazon Seller Central" },
      ]}
    />

    <Sub>Page Relationship Map</Sub>
    <P>
      Every page in Signal feeds information to or receives information from other pages.
      Here's a summary of how data flows:
    </P>
    <div className="overflow-x-auto rounded-sm border border-border my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-tint-medium">
            <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Page</th>
            <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Sends data to</th>
            <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Receives data from</th>
          </tr>
        </thead>
        <tbody className="text-muted-foreground">
          {pageRelationshipData.map(([page, sends, receives], i) => (
            <tr key={page} className={i % 2 === 0 ? "bg-card" : "bg-surface-tint-subtle"}>
              <td className="px-4 py-2 border-b border-border/50 font-medium text-foreground">{page}</td>
              <td className="px-4 py-2 border-b border-border/50">{sends}</td>
              <td className="px-4 py-2 border-b border-border/50">{receives}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <SectionTitle id="glossary">Glossary</SectionTitle>
    <div className="overflow-x-auto rounded-sm border border-border my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-tint-medium">
            <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border w-1/4">Term</th>
            <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Definition</th>
          </tr>
        </thead>
        <tbody className="text-muted-foreground">
          {glossaryData.map(([term, def], i) => (
            <tr key={term} className={i % 2 === 0 ? "bg-card" : "bg-surface-tint-subtle"}>
              <td className="px-4 py-2 border-b border-border/50 font-medium text-foreground">{term}</td>
              <td className="px-4 py-2 border-b border-border/50">{def}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);
